/** Trial banner / manage-subscription copy (#579). */

export type TrialConversion = 'will_convert' | 'bare_trial' | 'ending';

export type BillingStatusForBanner = {
  status?: string;
  subscription_tier?: string;
  trial_days_remaining?: number;
  trial_ends_at?: string | null;
  will_convert_at?: string | null;
  trial_conversion?: TrialConversion | null;
  stripe_subscription_id?: string | null;
  subscription_ends_at?: string | null;
  has_payment_method?: boolean;
};

export type TrialBannerView =
  | { kind: 'hidden' }
  | { kind: 'will_convert'; message: string; showSubscribe: false; dateLabel: string }
  | { kind: 'bare_trial'; message: string; showSubscribe: true; daysLeft: number }
  | { kind: 'ending'; message: string; showSubscribe: false; dateLabel: string };

export function formatBillingDateUtc(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function resolveTrialConversion(
  data: BillingStatusForBanner | null | undefined,
): TrialConversion | null {
  if (!data) return null;
  if (data.trial_conversion) return data.trial_conversion;
  const tier = data.status || data.subscription_tier;
  if (tier !== 'trial') return null;
  if (data.will_convert_at) return 'will_convert';
  if (data.stripe_subscription_id && data.subscription_ends_at) return 'ending';
  if (data.stripe_subscription_id) return 'will_convert';
  return 'bare_trial';
}

function daysLeftLabel(daysLeft: number): string {
  if (daysLeft === 0) return 'Last day';
  if (daysLeft === 1) return '1 day left';
  return `${daysLeft} days left`;
}

/**
 * Banner the app shows in the last 7 trial days.
 * Will-convert: subscriber message, never a Subscribe prompt.
 * Bare trial: today's "Trial ends in … / Subscribe now".
 * Ending (cancel_at_period_end): honest end notice, no Subscribe prompt.
 */
export function resolveTrialBannerView(
  data: BillingStatusForBanner | null | undefined,
): TrialBannerView {
  if (!data) return { kind: 'hidden' };
  const tier = data.status || data.subscription_tier;
  if (tier !== 'trial') return { kind: 'hidden' };

  const days = typeof data.trial_days_remaining === 'number' ? data.trial_days_remaining : null;
  const conversion = resolveTrialConversion(data);

  if (conversion === 'will_convert') {
    const dateLabel = formatBillingDateUtc(data.will_convert_at || data.trial_ends_at);
    if (!dateLabel) return { kind: 'hidden' };
    return {
      kind: 'will_convert',
      dateLabel,
      showSubscribe: false,
      message: `Your subscription is active — billing begins ${dateLabel}`,
    };
  }

  if (conversion === 'ending') {
    const dateLabel = formatBillingDateUtc(data.trial_ends_at || data.subscription_ends_at);
    if (!dateLabel || days == null || days > 7) return { kind: 'hidden' };
    return {
      kind: 'ending',
      dateLabel,
      showSubscribe: false,
      message: `Your subscription ends on ${dateLabel} — you will not be charged`,
    };
  }

  if (days == null || days > 7) return { kind: 'hidden' };
  return {
    kind: 'bare_trial',
    daysLeft: days,
    showSubscribe: true,
    message: `Trial ends in ${daysLeftLabel(days)}`,
  };
}

/** Manage-subscription trial card — same three classes as the banner. */
export function resolveManageTrialKind(
  sub: BillingStatusForBanner | null | undefined,
): TrialConversion | null {
  const tier = sub?.subscription_tier || sub?.status;
  if (tier !== 'trial') return null;
  return resolveTrialConversion(sub);
}
